import React, { useState } from 'react';

import {
  addYears,
  endOfDay,
  endOfWeek,
  isAfter,
  isBefore,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
  subYears
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { formatDate } from 'lib/helpers/dateUtils';
import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Button from '../Button/Button';
import DatePicker from '../DatePicker/DatePicker';
import Icon from '../Icon/Icon';
import Input from '../Input/Input';
import Popover from '../Popovers/Popover/Popover';
import Typography from '../Typography/Typography';

const MIN_START_DATE = startOfDay(subYears(new Date(), 150));
const MAX_END_DATE = endOfDay(addYears(new Date(), 150));

const clampToMinDate = (date) => {
  if (!date) return date;
  return isBefore(date, MIN_START_DATE) ? MIN_START_DATE : date;
};

const clampToMaxDate = (date) => {
  if (!date) return date;
  return isAfter(date, MAX_END_DATE) ? MAX_END_DATE : date;
};

const clampToBounds = (date) => clampToMaxDate(clampToMinDate(date));

export function getDateRange(unit, amount, timeZone) {
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();

  let startDate;
  if (unit === 'days') {
    const shifted = subDays(now, safeAmount);
    startDate = startOfDay(clampToBounds(shifted)).toISOString();
  } else if (unit === 'weeks') {
    const shifted = subWeeks(now, safeAmount);
    startDate = startOfDay(clampToBounds(shifted)).toISOString();
  } else if (unit === 'months') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - safeAmount);
    startDate = startOfDay(clampToBounds(d)).toISOString();
  } else {
    const shifted = subDays(now, safeAmount);
    startDate = startOfDay(clampToBounds(shifted)).toISOString();
  }
  const endDate = clampToMaxDate(endOfDay(now)).toISOString();
  return { startDate, endDate };
}

export function getDateRangeByUnit(unit, amount, timeZone) {
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();

  let shifted;
  if (unit === 'days') {
    shifted = subDays(now, safeAmount);
  } else if (unit === 'weeks') {
    shifted = subWeeks(now, safeAmount);
  } else {
    shifted = new Date(now);
    shifted.setMonth(shifted.getMonth() - safeAmount);
  }
  shifted = clampToBounds(shifted);
  let startDate, endDate;
  if (unit === 'weeks') {
    const weekStart = startOfWeek(shifted);
    startDate = clampToMinDate(weekStart).toISOString();
    endDate = clampToMaxDate(endOfDay(endOfWeek(shifted))).toISOString();
  } else {
    startDate = startOfDay(shifted).toISOString();
    endDate = clampToMaxDate(endOfDay(shifted)).toISOString();
  }
  return { startDate, endDate };
}

export const getDateOptions = (timeZone) => [
  { id: 'all-time', label: 'All Time', value: null },
  { id: 'today', label: 'Today', value: getDateRangeByUnit('days', 0, timeZone) },
  { id: 'yesterday', label: 'Yesterday', value: getDateRangeByUnit('days', 1, timeZone) },
  { id: 'last-week', label: 'Last Week', value: getDateRangeByUnit('weeks', 1, timeZone) },
  { id: 'last-30-days', label: 'Last 30 Days', value: getDateRange('days', 30, timeZone) },
  { id: 'last-60-days', label: 'Last 60 Days', value: getDateRange('days', 60, timeZone) },
  { id: 'last-90-days', label: 'Last 90 Days', value: getDateRange('days', 90, timeZone) },
  {
    id: 'custom-date-range',
    label: 'Custom date range',
    value: null
  }
];

const Option = ({ label, value, onClick, option, selectedOption }) => {
  return (
    <div
      className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50"
      onClick={() => onClick(option)}>
      <Typography className="cursor-pointer whitespace-nowrap" size="small">
        {label}
      </Typography>
      {selectedOption?.value === value && <Icon icon="check-linear" color="neutral" shadow={700} />}
    </div>
  );
};

export const DateRangeContent = ({
  onChange,
  value,
  onClose = () => {},
  className,
  dataKey,
  formula
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const timezone = useTimezone();

  const handleChange = (event) => {
    const value = event.target.value;
    setInput(value);

    const number = parseInt(value, 10);

    if (!isNaN(number) && number >= 0) {
      setSuggestions([
        {
          label: number === 1 ? 'Yesterday' : `Last ${number} Days`,
          value: getDateRange('days', number)
        },
        {
          label: number === 1 ? 'Last Week' : `Last ${number} Weeks`,
          value: getDateRange('weeks', number)
        },
        {
          label: number === 1 ? 'Last Month' : `Last ${number} Months`,
          value: getDateRange('months', number)
        }
      ]);
    } else {
      setSuggestions([]);
    }
  };

  const onSelectOptionHandler = (option, closePopover) => {
    setInput('');
    setSuggestions([]);
    if (!option.value) {
      onChange(
        { id: option.id, startDate: null, endDate: null, timezone, label: option.label },
        dataKey
      );
      closePopover();
      return;
    }
    onChange({ id: option.id, ...option.value, label: option.label, timezone }, dataKey, formula);
    closePopover();
  };

  const onRangeChange = (ranges) => {
    if (!ranges?.[0] || !ranges?.[1]) return;
    const [startDate, endDate] = ranges;
    onChange({ id: 'custom-date-range', startDate, endDate, timezone }, dataKey, formula);
    onClose();
  };

  const toRenderOptions = ia(suggestions)
    ? suggestions
    : getDateOptions(timezone).filter((o) => o.id !== 'custom-date-range');

  return (
    <div className={className}>
      <div className="flex flex-col gap-y-2">
        <Input
          inputWrapperClassName="w-full border-0 border-b border-neutral-200 rounded-none"
          placeholder="Custom range: 2d, 4d, 8weeks..."
          value={input}
          onChange={handleChange}
        />
        {toRenderOptions.map((option) => (
          <Option
            key={option.id}
            label={option.label}
            value={option.value}
            onClick={() => onSelectOptionHandler(option, onClose)}
            selectedOption={value}
          />
        ))}
        <DatePicker
          isDateRange
          isTimeRange
          isTimezone
          value={value?.startDate && value?.endDate ? [value.startDate, value.endDate] : null}
          onChange={onRangeChange}
          customButtonTrigger={
            <div className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50">
              <Typography className="cursor-pointer whitespace-nowrap" size="small">
                Custom range
              </Typography>
            </div>
          }
        />
      </div>
    </div>
  );
};

const DateRangeSelector = ({
  onChange,
  value,
  buttonClassName,
  buttonTextClassName,
  buttonIcon,
  buttonIconColor = 'neutral',
  position
}) => {
  const timezone = useTimezone();

  const buttonText =
    value?.id !== 'custom-date-range' ? (
      getDateOptions(timezone).find((option) => option.id === value?.id)?.label ||
      value?.label ||
      'All Time'
    ) : (
      <>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(value?.startDate, timezone)}
        </label>
        <span className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>-</span>
        <label className={cn('cursor-pointer', buttonTextClassName || 'text-neutral-800')}>
          {formatDate(value?.endDate, timezone)}
        </label>
      </>
    );

  return (
    <Popover
      panelClassName="p-0 min-w-[244px] w-fit"
      position={position}
      isFixed
      customButtonTrigger={({ open }) => (
        <Button
          variant="tertiary"
          size="xs"
          text={buttonText}
          icon={buttonIcon}
          className={cn('whitespace-nowrap', open && 'bg-neutral-100', buttonClassName)}
          textClassName={buttonTextClassName}
          iconColor={buttonIconColor}
        />
      )}>
      {({ closePopover }) => (
        <DateRangeContent onChange={onChange} value={value} onClose={closePopover} />
      )}
    </Popover>
  );
};

export default DateRangeSelector;
